import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RequestsOverTime, VizStyle } from "../usage/charts";
import {
  API_USAGE_ALL_KEYS,
  apiUsageChange,
  type ApiUsageDrop,
  type ApiUsageKey,
  type ApiUsageLatest,
  type ApiUsagePeriod,
} from "../../../../../../shared/index";

/**
 * The usage monitor page — are the External API clients still calling?
 *
 * Everything here is read from what the monitor (apps/b2b-widgets/monitor,
 * src/api-usage, a GitHub Actions job once a day) wrote to blob. A key's calls are
 * its App Insights share of each hour, scaled to the APIM gateway count of that
 * hour. A drop: the last 7 (or 28) days are more than 20% (and 10k calls a week)
 * under the key's usual level, the lowest week (or month) of the 8 weeks before, so
 * a spike that ends is not a drop. A key that starts or stops dropping is posted to one GitHub issue.
 */

/** The job runs once a day; 30 hours without a new document means it did not run. */
const STALE_HOURS = 30;

const n = (x: number) => x.toLocaleString("en-US");
const pct = (x: number | null) => (x === null ? "–" : `${x > 0 ? "+" : ""}${(x * 100).toFixed(1)}%`);

export default function B2bApiUsage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("key") ?? API_USAGE_ALL_KEYS;
  const latest = useQuery({ queryKey: ["b2b", "api-usage", "latest"], queryFn: api.b2b.apiUsage.latest });
  const data = latest.data ?? null;
  const stale = data ? Date.now() - Date.parse(data.updatedAt) > STALE_HOURS * 3_600_000 : false;

  return (
    <div className="space-y-6">
      <VizStyle />
      <div>
        <Link className="text-sm text-muted-foreground underline" to="/dashboard/b2b/external-api">
          External API
        </Link>
        <h1 className="text-2xl font-semibold">Usage monitor</h1>
        <p className="text-sm text-muted-foreground">
          Calls per Client Key on api.tipranks.com{data ? `, through ${data.through} (UTC)` : ""}. Updated once a day.
        </p>
      </div>

      {latest.error ? <Banner>{String((latest.error as Error).message)}</Banner> : null}
      {latest.isSuccess && !data ? (
        <Banner>No results yet. The External API Usage Monitor workflow writes here once it has run.</Banner>
      ) : null}
      {stale ? <Banner>No monitor run for over {STALE_HOURS} hours. Check the External API Usage Monitor workflow.</Banner> : null}
      {data && !data.calibrated ? (
        <Banner>
          Some days are raw Application Insights estimates, not scaled to the APIM gateway count (the monitor could not read
          the APIM metric). Sampling makes those numbers too high by 1.2x to 2.6x, and changes between weeks unreliable.
        </Banner>
      ) : null}

      {data ? (
        <>
          <Summary data={data} />
          <Drops data={data} onPick={(key) => setParams((p) => (p.set("key", key), p))} />
          <DailyChart selected={selected} onReset={() => setParams((p) => (p.delete("key"), p))} />
          <KeyTable data={data} selected={selected} onPick={(key) => setParams((p) => (p.set("key", key), p))} />
        </>
      ) : latest.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : null}
    </div>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-warning/50 bg-warning/10 px-4 py-3 text-sm">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

/** Red only for a real drop (`alert`); a fall from a spike stays grey. */
function Change({ value, alert = false }: { value: number | null; alert?: boolean }) {
  return (
    <span className={cn("tabular-nums", alert ? "font-semibold text-destructive" : value !== null && value < 0 ? "text-muted-foreground" : "")}>
      {pct(value)}
    </span>
  );
}

function Summary({ data }: { data: ApiUsageLatest }) {
  const tiles: Array<{ label: string; period: ApiUsagePeriod; hint: string }> = [
    { label: "Last 7 days", period: "week", hint: "vs the 7 days before" },
    { label: "Last 28 days", period: "month", hint: "vs the 28 days before" },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      {tiles.map((t) => (
        <div key={t.period} className="rounded-xl border bg-card p-4">
          <p className="text-sm text-muted-foreground">{t.label}, every key</p>
          <p className="text-2xl font-semibold tabular-nums">{n(data.total[t.period].current)}</p>
          <p className="text-xs text-muted-foreground">
            <Change value={apiUsageChange(data.total[t.period])} alert={data.alerted[data.total.key]?.includes(t.period)} /> {t.hint}
          </p>
        </div>
      ))}
      <div className="rounded-xl border bg-card p-4">
        <p className="text-sm text-muted-foreground">Keys</p>
        <p className="text-2xl font-semibold tabular-nums">{data.keys.length}</p>
        <p className="text-xs text-muted-foreground">with a call in the last 56 days</p>
      </div>
    </div>
  );
}

function Drops({ data, onPick }: { data: ApiUsageLatest; onPick: (key: string) => void }) {
  const byKey = useMemo(() => {
    const m = new Map<string, ApiUsageDrop[]>();
    for (const d of data.drops) m.set(d.key, [...(m.get(d.key) ?? []), d]);
    return [...m];
  }, [data.drops]);
  const limit = `${(data.threshold * 100).toFixed(0)}%`;

  return (
    <section className="rounded-xl border bg-card">
      <div className="border-b px-4 py-3">
        <h2 className="font-semibold">Drops ({byKey.length})</h2>
        <p className="text-xs text-muted-foreground">
          The last 7 days (or 28) are more than {limit} under the key's usual level: the lowest week (or month) of the 8
          weeks before{data.minWeeklyLoss ? `, and at least ${n(data.minWeeklyLoss)} calls a week under it` : ""}. A fall back to
          the usual level after a spike is not a drop. Keys under {n(data.minWeekly)} calls a week are not judged.
          {data.inactive?.length ? ` Keys inactive in ExternalApiDB are not judged or listed: ${data.inactive.join(", ")}.` : ""} A key that starts or stops dropping is posted to the GitHub issue “[external-api-usage] drops”.
        </p>
      </div>
      {byKey.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted-foreground">No key fell more than {limit}.</p>
      ) : (
        <ul className="divide-y">
          {byKey.map(([key, drops]) => (
            <li key={key} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-2 text-sm">
              <button className="font-mono font-medium underline-offset-2 hover:underline" onClick={() => onPick(key)}>
                {key}
              </button>
              {drops.map((d) => (
                <span key={d.period} className="text-muted-foreground">
                  {d.period === "week" ? "Week" : "Month"}: {n(d.to)} vs usual {n(d.usual ?? d.from)}{" "}
                  <Change value={(d.to - (d.usual ?? d.from)) / (d.usual ?? d.from)} alert />
                  {d.usual !== undefined && d.usual !== d.from ? ` (the ${d.period} before: ${n(d.from)})` : null}
                </span>
              ))}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DailyChart({ selected, onReset }: { selected: string; onReset: () => void }) {
  const daily = useQuery({ queryKey: ["b2b", "api-usage", "daily", selected], queryFn: () => api.b2b.apiUsage.daily(selected) });
  const points = (daily.data ?? []).map((d) => ({ timestamp: `${d.day}T00:00:00Z`, requests: d.requests }));
  return (
    <section className="rounded-xl border bg-card p-4">
      <div className="mb-2 flex items-baseline justify-between gap-4">
        <h2 className="font-semibold">
          Calls per day: <span className="font-mono">{selected}</span>
        </h2>
        {selected !== API_USAGE_ALL_KEYS ? (
          <button className="text-sm text-muted-foreground underline" onClick={onReset}>
            Every key
          </button>
        ) : null}
      </div>
      {daily.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : <RequestsOverTime points={points} daily />}
    </section>
  );
}

type SortKey = "key" | "week" | "weekChange" | "month" | "monthChange";

function KeyTable({ data, selected, onPick }: { data: ApiUsageLatest; selected: string; onPick: (key: string) => void }) {
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("week");
  const rows = useMemo(() => {
    const value = (k: ApiUsageKey): number | string => {
      if (sort === "key") return k.key.toLowerCase();
      if (sort === "week") return -k.week.current;
      if (sort === "month") return -k.month.current;
      // Biggest falls first; keys with nothing to compare with last.
      return apiUsageChange(sort === "weekChange" ? k.week : k.month) ?? Infinity;
    };
    const f = filter.trim().toLowerCase();
    return data.keys
      .filter((k) => !f || k.key.toLowerCase().includes(f))
      .sort((a, b) => {
        const [x, y] = [value(a), value(b)];
        return x < y ? -1 : x > y ? 1 : a.key.localeCompare(b.key);
      });
  }, [data.keys, filter, sort]);
  const head = (key: SortKey, label: string, right = true) => (
    <TableHead className={cn(right && "text-right")}>
      <button className={cn(sort === key && "font-semibold text-foreground")} onClick={() => setSort(key)}>
        {label}
      </button>
    </TableHead>
  );

  return (
    <section className="space-y-3">
      <input
        className="w-full max-w-xs rounded-md border bg-background px-3 py-1.5 text-sm"
        placeholder="Filter keys"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      <div className="overflow-x-auto rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              {head("key", "Client Key", false)}
              {head("week", "Last 7 days")}
              <TableHead className="text-right">7 days before</TableHead>
              {head("weekChange", "Week")}
              {head("month", "Last 28 days")}
              <TableHead className="text-right">28 days before</TableHead>
              {head("monthChange", "Month")}
              <TableHead>Last call</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((k) => (
              <TableRow
                key={k.key}
                className={cn("cursor-pointer", k.key === selected && "bg-muted", data.alerted[k.key] && "bg-destructive/5")}
                onClick={() => onPick(k.key)}
              >
                <TableCell className="font-mono text-xs">{k.key}</TableCell>
                <TableCell className="text-right tabular-nums">{n(k.week.current)}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{n(k.week.previous)}</TableCell>
                <TableCell className="text-right">
                  <Change value={apiUsageChange(k.week)} alert={data.alerted[k.key]?.includes("week")} />
                </TableCell>
                <TableCell className="text-right tabular-nums">{n(k.month.current)}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{n(k.month.previous)}</TableCell>
                <TableCell className="text-right">
                  <Change value={apiUsageChange(k.month)} alert={data.alerted[k.key]?.includes("month")} />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{k.lastSeen ?? "–"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
